import { useState, useEffect, useRef } from 'react'
import { getPhysiognomy, uploadPhysiognomyPhoto, analyzePhysiognomyStart, pollThreadsJob } from '../utils/api'
import { getModelPref } from '../utils/modelPrefs'
import ModelToggle from './ModelToggle'
import { C, font } from './ProfileResult'

function useIsMobile(bp = 640) {
  const [v, setV] = useState(() => window.innerWidth < bp)
  useEffect(() => {
    let t; const h = () => { clearTimeout(t); t = setTimeout(() => setV(window.innerWidth < bp), 80) }
    window.addEventListener('resize', h); return () => { clearTimeout(t); window.removeEventListener('resize', h) }
  }, [bp]); return v
}

const glass = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  padding: '20px 24px',
  marginBottom: 16,
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => resolve(e.target.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function MatchBadge({ match, note }) {
  if (typeof match !== 'boolean') return null
  const color = match ? 'rgba(77,200,138,0.85)' : 'rgba(220,140,60,0.85)'
  const bg = match ? 'rgba(77,200,138,0.08)' : 'rgba(220,140,60,0.08)'
  const border = match ? 'rgba(77,200,138,0.25)' : 'rgba(220,140,60,0.25)'
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6,
      padding: '2px 8px', borderRadius: 5, background: bg, border: `1px solid ${border}`,
    }}>
      <span style={{ fontFamily: font.mono, fontSize: 10, color }}>{match ? '✓' : '△'}</span>
      {note && <span style={{ fontFamily: font.body, fontSize: 11, color: C.textMuted }}>{note}</span>}
    </div>
  )
}

function TraitRow({ feature, description, match, match_note }) {
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 8, marginBottom: 8,
      background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}`,
    }}>
      <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
        {feature}
      </div>
      <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.55 }}>
        {description}
      </div>
      <MatchBadge match={match} note={match_note} />
    </div>
  )
}

function MbtiBlock({ reading }) {
  if (!reading?.type) return null
  return (
    <div style={{
      marginTop: 14, marginBottom: 14, padding: '12px 16px', borderRadius: 8,
      background: 'rgba(184,189,212,0.05)', border: `1px solid ${C.border}`,
    }}>
      <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim, marginBottom: 8 }}>
        MBTI по лицу
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: font.heading, fontSize: 24, fontWeight: 400, color: C.silverHi }}>{reading.type}</span>
        {typeof reading.confidence === 'number' && (
          <span style={{ fontFamily: font.mono, fontSize: 10, color: C.textDim }}>{reading.confidence}% уверенность</span>
        )}
      </div>
      {reading.reasoning && (
        <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.55, marginBottom: 8 }}>
          {reading.reasoning}
        </div>
      )}
      <MatchBadge match={reading.matches_real} note={reading.match_note} />
    </div>
  )
}

export default function PhysiognomyTab({ userId = 'user', isOwner = false }) {
  const isMobile = useIsMobile()
  const fileInputRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [photo, setPhoto] = useState(null)
  const [data, setData] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    getPhysiognomy(userId)
      .then(d => {
        if (d.photo) setPhoto(d.photo)
        if (!d.empty && !d.error) setData(d)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [userId])

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setError(null); setUploading(true)
    try {
      const dataUrl = await readFileAsDataUrl(file)
      const res = await uploadPhysiognomyPhoto(userId, dataUrl)
      setPhoto(res.photo)
      setData(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const analyze = async () => {
    setAnalyzing(true); setError(null)
    try {
      const jobId = await analyzePhysiognomyStart(userId, getModelPref('physiognomy'))
      const result = await pollThreadsJob(jobId, null, 5 * 60 * 1000)
      setData(result)
    } catch (err) {
      setError(err.message)
    } finally {
      setAnalyzing(false)
    }
  }

  if (loading) {
    return <div style={{ padding: '60px 0', textAlign: 'center', fontFamily: font.mono, fontSize: 11, color: C.textDim }}>Загрузка...</div>
  }

  if (!isOwner && !photo) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontFamily: font.heading, fontSize: 22, color: C.text, marginBottom: 8, fontWeight: 400 }}>Физиогномика недоступна</div>
        <p style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted }}>Фото ещё не загружено.</p>
      </div>
    )
  }

  return (
    <div style={{ paddingBottom: 60 }}>
      <div style={{ ...glass, display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {photo ? (
            <img src={photo} alt="" style={{ width: 72, height: 72, borderRadius: 10, objectFit: 'cover', border: `1px solid ${C.border}` }} />
          ) : (
            <div style={{ width: 72, height: 72, borderRadius: 10, border: `1px dashed ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: font.mono, fontSize: 24, color: C.textDim }}>?</div>
          )}
          <div>
            <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim, marginBottom: 6 }}>
              Физиогномика
            </div>
            <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, maxWidth: 360 }}>
              Западная традиция (Лафатер) + китайская мяньсян — сопоставление с реальным профилем. Развлекательный жанр, не наука.
            </div>
          </div>
        </div>
        {isOwner && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{
                  padding: '8px 18px', borderRadius: 8, fontFamily: font.body, fontSize: 12,
                  background: 'rgba(184,189,212,0.08)', border: `1px solid ${C.border}`,
                  color: C.textMuted, cursor: uploading ? 'default' : 'pointer',
                }}
              >
                {uploading ? '⏳ Загружаю...' : photo ? '↻ Другое фото' : '↑ Загрузить фото'}
              </button>
              {photo && (
                <button onClick={analyze} disabled={analyzing} style={{
                  padding: '8px 18px', borderRadius: 8, fontFamily: font.body, fontSize: 12,
                  background: 'rgba(184,189,212,0.12)', border: `1px solid ${C.borderHi}`,
                  color: C.accent, cursor: analyzing ? 'default' : 'pointer',
                }}>
                  {analyzing ? '⏳ Анализирую...' : data ? '↻ Пересчитать' : '⬡ Проанализировать'}
                </button>
              )}
              <ModelToggle prefKey="physiognomy" />
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: '10px 16px', borderRadius: 8, background: 'rgba(212,90,90,0.08)', border: '1px solid rgba(212,90,90,0.25)', color: 'rgba(220,100,100,0.9)', fontFamily: font.body, fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {!data && !error && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontFamily: font.mono, fontSize: 32, color: C.textDim, marginBottom: 16, opacity: 0.3 }}>⬡</div>
          <p style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted }}>
            {photo ? 'Фото загружено — запустите анализ.' : 'Загрузите фото лица, чтобы начать.'}
          </p>
        </div>
      )}

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
          <div style={glass}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
              <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim }}>
                Западная традиция
              </div>
              {typeof data.western?.overall_match_percent === 'number' && (
                <span style={{ fontFamily: font.mono, fontSize: 10, color: C.accent }}>{data.western.overall_match_percent}% совпадение</span>
              )}
            </div>
            <div style={{ fontFamily: font.heading, fontSize: 20, color: C.silverHi, marginBottom: 14, fontWeight: 400 }}>
              Лафатер
            </div>
            {data.western?.face_shape && (
              <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginBottom: 14, lineHeight: 1.55 }}>
                {data.western.face_shape}
              </div>
            )}
            {(data.western?.traits || []).map((t, i) => <TraitRow key={i} {...t} />)}
            <MbtiBlock reading={data.western?.mbti_reading} />
            {data.western?.summary && (
              <div style={{ marginTop: 10, fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.6, fontStyle: 'italic' }}>
                {data.western.summary}
              </div>
            )}
          </div>

          <div style={glass}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
              <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim }}>
                Китайская традиция
              </div>
              {typeof data.mian_xiang?.overall_match_percent === 'number' && (
                <span style={{ fontFamily: font.mono, fontSize: 10, color: C.accent }}>{data.mian_xiang.overall_match_percent}% совпадение</span>
              )}
            </div>
            <div style={{ fontFamily: font.heading, fontSize: 20, color: C.silverHi, marginBottom: 14, fontWeight: 400 }}>
              面相 Мяньсян
            </div>
            {data.mian_xiang?.three_zones && (
              <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
                {['upper', 'middle', 'lower'].map(zk => {
                  const z = data.mian_xiang.three_zones[zk]
                  if (!z) return null
                  return (
                    <div key={zk} style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}` }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 3 }}>
                        <span style={{ fontFamily: font.body, fontSize: 12, color: C.text }}>{z.name}</span>
                        <span style={{ fontFamily: font.mono, fontSize: 10, color: C.textDim }}>{z.period}</span>
                      </div>
                      <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.5 }}>{z.description}</div>
                      <MatchBadge match={z.match} note={z.match_note} />
                    </div>
                  )
                })}
              </div>
            )}
            {(data.mian_xiang?.traits || []).map((t, i) => <TraitRow key={i} {...t} />)}
            <MbtiBlock reading={data.mian_xiang?.mbti_reading} />
            {data.mian_xiang?.summary && (
              <div style={{ marginTop: 10, fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.6, fontStyle: 'italic' }}>
                {data.mian_xiang.summary}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
